import type { AttendanceRecord } from '@/mocks/db/schema';

import { AttendanceTableColumns } from '../../-external-ta/type';
import type { ClockingRecordsDailyRow } from '../types';

type AttendanceStatus = AttendanceRecord['status'];

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function workingMinutes(start: string | null, end: string | null): number {
  if (!start || !end) return 0;
  return Math.max(0, timeToMinutes(end) - timeToMinutes(start));
}

function statusToErrorCodes(status: AttendanceStatus): string[] {
  switch (status) {
    case 'late':
      return ['late'];
    case 'absent':
      return ['absent'];
    case 'early_leave':
      return ['early_leave'];
    case 'paid_leave':
      return ['paid_leave'];
    case 'sick_leave':
      return ['sick_leave'];
    case 'holiday':
      return ['holiday'];
    default:
      return [];
  }
}

/**
 * Dexie の AttendanceRecord を日次サマリ行へ変換（OpenAPI 型に依存しない）
 */
export function mapAttendanceRecordToDailyRow(
  record: AttendanceRecord,
): ClockingRecordsDailyRow {
  const [y, mo, d] = record.date.split('-').map(Number);
  const dateObj = new Date(y ?? 1970, (mo ?? 1) - 1, d ?? 1);

  const clockIns =
    record.actualStart !== null
      ? [
          {
            timeRecordId: `${record.id}-in`,
            time: record.actualStart,
            isEdited: false,
          },
        ]
      : [];

  const clockOuts =
    record.actualEnd !== null
      ? [
          {
            timeRecordId: `${record.id}-out`,
            time: record.actualEnd,
            isEdited: false,
          },
        ]
      : [];

  const status: AttendanceStatus = record.status ?? 'present';
  const errorCodes = statusToErrorCodes(status);

  return {
    [AttendanceTableColumns.PERSON]: {
      id: record.employeeId,
      name: record.employeeName,
      linkTo: `/attendances/clocking-records/${record.employeeId}`,
    },
    attendanceStatus: status,
    [AttendanceTableColumns.DATE]: dateObj,
    [AttendanceTableColumns.CLOCK_INS]: clockIns,
    [AttendanceTableColumns.CLOCK_OUTS]: clockOuts,
    [AttendanceTableColumns.REST_TIMES]: {
      totalRestTimeMinutes: 0,
      restTimes: [],
      isEdited: false,
    },
    [AttendanceTableColumns.TOTAL_WORKING_TIME_MINUTES]: workingMinutes(
      record.actualStart,
      record.actualEnd,
    ),
    [AttendanceTableColumns.IS_VALID]: errorCodes.length === 0,
    [AttendanceTableColumns.ERRORS]: errorCodes,
    workplaceName: record.workplaceName,
    approvalStatus: record.approvalStatus,
    overtimeMinutes: record.overtimeMinutes,
    lateNightWorkMinutes: 0,
    rowKey: `${record.employeeId}__${record.date}`,
  };
}
